import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ApiError } from "../../api/client";
import type { Challenge, LoginOutcome, ProviderCard } from "../../api/auth";
import type { Me, Surface } from "../../api/types";
import { Notice } from "../../design/components";
import { useAuth } from "../../auth/AuthProvider";
import { providers, toMe } from "../../auth/session";
import { ChallengeStep } from "./ChallengeStep";
import { useHubBranding } from "./useHubBranding";
import { AuthSplit } from "./AuthSplit";

/** Only same-origin paths are followed: no scheme-relative, backslash or control-character tricks. */
function safeNext(raw: string | null): string {
  if (!raw || !raw.startsWith("/")) return "/";
  if (raw[1] === "/" || raw[1] === "\\") return "/";
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(raw)) return "/";
  return raw;
}

/** The session's surface decides the home; `next` is honoured only on its own side. */
export function landing(surface: Surface, raw: string | null): string {
  const next = safeNext(raw);
  const portalNext = next === "/portal" || next.startsWith("/portal/");
  if (surface === "portal") return portalNext ? next : "/portal";
  return portalNext ? "/" : next;
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError && error.status === 429) return "Too many attempts, try again later";
  // The API already says why a handoff was refused (expired, reused, wrong browser): "Start the sign-in again".
  if (error instanceof ApiError && error.code.startsWith("oidc_")) return error.message;
  if (error instanceof ApiError && error.status === 401) {
    return "Sign-in failed. Check your details and try again.";
  }
  if (error instanceof ApiError && error.code === "no_membership") {
    return "This account has no company access. Ask your administrator for an invitation.";
  }
  if (error instanceof ApiError) return error.message;
  return "Sign-in failed. Try again.";
}

const COPY: Record<Surface, { title: string; sub: string }> = {
  hub: { title: "Sign in", sub: "Group console" },
  portal: { title: "Sign in to your company", sub: "Company portal" },
};

/** Where the browser goes for single sign-on: a full navigation, the API redirects to the provider. */
export const OIDC_START_PATH = "/api/auth/oidc/start";
const OIDC_FRAGMENT_KEY = "oidc";

/** Takes the `#oidc=<handoff>` fragment the callback left and clears it before anything can read it again. */
export function takeHandoff(): string | null {
  const hash = window.location.hash;
  if (!hash.startsWith(`#${OIDC_FRAGMENT_KEY}=`)) return null;
  window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
  try {
    return decodeURIComponent(hash.slice(OIDC_FRAGMENT_KEY.length + 2)) || null;
  } catch {
    return null; // a malformed fragment is not a handoff; the password form shows as usual
  }
}

export function SignIn({ surface }: { surface: Surface }) {
  // Both sign-in screens carry the fixed Infinity title and favicon.
  useHubBranding("hub");
  const { signIn, signInWithHandoff } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const emailRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [provider, setProvider] = useState<ProviderCard | null>(null);
  // Strict-mode double-mount must not spend the handoff twice: the fragment is gone after the first read.
  const [handoff] = useState<string | null>(takeHandoff);
  const [exchanging, setExchanging] = useState(handoff !== null);
  const spent = useRef(false);

  useEffect(() => {
    let live = true;
    providers()
      .then((list) => {
        if (live) setProvider(list.items[0] ?? null);
      })
      .catch(() => {
        if (live) setProvider(null);
      });
    return () => {
      live = false;
    };
  }, []);

  useLayoutEffect(() => {
    const previous = document.body.className;
    document.body.className = `surface-${surface}`;
    return () => {
      document.body.className = previous;
    };
  }, [surface]);

  useEffect(() => {
    if (!challenge) emailRef.current?.focus();
  }, [challenge]);

  function finish(me: Me) {
    navigate(landing(me.surface, params.get("next")), { replace: true });
  }

  function restart(message: string) {
    setChallenge(null);
    setPassword("");
    setFailure(message);
  }

  /** Both legs answer the same union: a session lands, anything else is the second step. */
  function settle(outcome: LoginOutcome) {
    if (outcome.status === "ok") {
      finish(toMe(outcome));
      return;
    }
    setChallenge(outcome);
  }

  // The SSO handoff is spent exactly once; a stale or reused one falls back to the password form.
  // No cleanup gate: StrictMode's simulated unmount must not discard the outcome of the one flight.
  useEffect(() => {
    if (handoff === null || spent.current) return;
    spent.current = true;
    signInWithHandoff(handoff)
      .then(settle)
      .catch((error: unknown) => setFailure(messageFor(error)))
      .finally(() => setExchanging(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoff]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setFailure(null);
    try {
      settle(await signIn(email, password));
    } catch (error) {
      setFailure(messageFor(error));
      emailRef.current?.focus();
    } finally {
      setPending(false);
    }
  }

  function startSso() {
    window.location.assign(OIDC_START_PATH);
  }

  const content = (
    <>
      {challenge ? (
          <ChallengeStep challenge={challenge} onDone={finish} onExpired={restart} />
        ) : exchanging ? (
          <div className="card signin-card" role="status" aria-live="polite">
            <div className="card-body stack">
              <p className="muted">Completing your sign-in…</p>
            </div>
          </div>
        ) : (
          <form className="card signin-card" onSubmit={onSubmit}>
            <div className="card-body stack">
              {failure ? <Notice kind="fail">{failure}</Notice> : null}
              <label className="field" htmlFor="signin-email">
                <span>Email</span>
                <input
                  id="signin-email"
                  ref={emailRef}
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label className="field" htmlFor="signin-password">
                <span>Password</span>
                <input
                  id="signin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>
              <button className="btn" type="submit" disabled={pending}>
                Sign in
              </button>
              {provider ? (
                <button className="btn btn-ghost signin-sso" type="button" onClick={startSso} disabled={pending}>
                  Sign in with {provider.name}
                </button>
              ) : null}
            </div>
          </form>
        )}
    </>
  );

  if (surface === "hub") {
    return (
      <AuthSplit title="Welcome back" welcome="Sign in to the group console to continue.">
        {content}
      </AuthSplit>
    );
  }

  return (
    <AuthSplit
      title={COPY[surface].title}
      welcome="Use your company account to continue."
      eyebrow="Company portal"
      headline="Your company's e-invoices, tracked from sent to archived."
    >
        {challenge ? (
          <ChallengeStep challenge={challenge} onDone={finish} onExpired={restart} />
        ) : exchanging ? (
          <div className="card signin-card" role="status" aria-live="polite">
            <div className="card-body stack">
              <p className="muted">Completing your sign-in…</p>
            </div>
          </div>
        ) : (
          <form className="card signin-card" onSubmit={onSubmit}>
            <div className="card-body stack">
              {failure ? <Notice kind="fail">{failure}</Notice> : null}
              <label className="field" htmlFor="signin-email">
                <span>Email</span>
                <input
                  id="signin-email"
                  ref={emailRef}
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label className="field" htmlFor="signin-password">
                <span>Password</span>
                <input
                  id="signin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>
              <button className="btn" type="submit" disabled={pending}>
                Sign in
              </button>
              {provider ? (
                <button className="btn btn-ghost signin-sso" type="button" onClick={startSso} disabled={pending}>
                  Sign in with {provider.name}
                </button>
              ) : null}
            </div>
          </form>
        )}
    </AuthSplit>
  );
}
